import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { createTrack } from '../../../api/SegmentApi';
import { Tenant } from '../../../interfaces';

interface PageTrackingUser {
  email: string;
  tenantId: string;
}

const QUARTILES = [25, 50, 75, 100] as const;

const snapToScrollQuartile = (rawPct: number): number => {
  if (rawPct >= 100) return 100;
  if (rawPct >= 75) return 75;
  if (rawPct >= 50) return 50;
  if (rawPct >= 25) return 25;
  return 0;
};

export const usePageTracking = (
  pageName: string,
  openEvent: string,
  closedEvent: string,
  tenant: Tenant,
  loggedUser: PageTrackingUser,
  isSentinels = false,
): void => {
  const sessionId = useRef('');
  const activeTimeMs = useRef(0);
  const lastVisibleAt = useRef(Date.now());
  const maxScrollQuartile = useRef(0);
  const hasFired = useRef(false);

  useEffect(() => {
    if (!loggedUser?.email || !loggedUser?.tenantId) return;

    hasFired.current = false;
    activeTimeMs.current = 0;
    lastVisibleAt.current = Date.now();
    maxScrollQuartile.current = 0;
    sessionId.current = uuidv4();

    const basePayload = {
      page_name: pageName,
      session_id: sessionId.current,
      plan: tenant.info.plan,
      organizationName: tenant.info.name,
      user: loggedUser.email,
      tenantId: loggedUser.tenantId,
      fireAndForget: true,
    };

    createTrack(tenant, openEvent, basePayload);

    const updateQuartile = (pct: number): void => {
      const quartile = snapToScrollQuartile(pct);
      if (quartile > maxScrollQuartile.current) {
        maxScrollQuartile.current = quartile;
      }
    };

    // IO-based scroll: auto-resized cross-origin iframe has scrollHeight === innerHeight, so native scroll events are unusable.
    let cleanupIO: (() => void) | null = null;

    const container = document.querySelector<HTMLElement>('.main-base-template-content');

    if (isSentinels && container && !container.dataset.tracking) {
      container.dataset.tracking = 'true';

      // Body sentinels: scrollY=0 in auto-resized iframe, so getBoundingClientRect().top is the absolute document Y.
      const sentinels = QUARTILES.map(() => {
        const div = document.createElement('div');
        div.style.cssText = 'position:absolute;left:0;width:1px;height:1px;pointer-events:none;';
        document.body.appendChild(div);
        return div;
      });

      const positionSentinels = () => {
        const containerTop = container.getBoundingClientRect().top + window.scrollY;
        const h = container.scrollHeight;
        QUARTILES.forEach((pct, i) => {
          sentinels[i].style.top = `${Math.max(0, containerTop + Math.floor((h * pct) / 100) - 1)}px`;
        });
      };

      positionSentinels();

      const resizeObs = new ResizeObserver(positionSentinels);
      resizeObs.observe(container);

      const prevIntersecting = new Set<HTMLElement>();
      let initDone = false;
      // Counter waits for every sentinel's initial IO report before treating intersections as scroll.
      let initRemaining = sentinels.length;

      const intersectObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const target = entry.target as HTMLDivElement;

            if (!initDone) {
              if (entry.isIntersecting) {
                prevIntersecting.add(target);
              } else {
                prevIntersecting.delete(target);
              }
              initRemaining--;
              return;
            }

            const wasVisible = prevIntersecting.has(target);
            if (entry.isIntersecting) {
              prevIntersecting.add(target);
              if (!wasVisible) {
                const idx = sentinels.indexOf(target);
                if (idx >= 0) updateQuartile(QUARTILES[idx]);
              }
            } else {
              prevIntersecting.delete(target);
            }
          });
          if (!initDone && initRemaining <= 0) initDone = true;
        },
        { threshold: 0 },
      );

      sentinels.forEach((s) => intersectObs.observe(s));

      cleanupIO = () => {
        intersectObs.disconnect();
        resizeObs.disconnect();
        sentinels.forEach((s) => s.remove());
        container.removeAttribute('data-tracking');
      };
    }

    // Scroll fallback only when IO is unavailable (standalone / dev mode without container).
    let cleanupScroll: (() => void) | null = null;

    if (cleanupIO === null) {
      const onScroll = (event: Event) => {
        let scrollTop: number;
        let scrollHeight: number;
        let clientHeight: number;

        const target = event.target;
        if (
          target instanceof Element &&
          target !== document.documentElement &&
          target !== document.body
        ) {
          scrollTop = target.scrollTop;
          scrollHeight = target.scrollHeight;
          clientHeight = target.clientHeight;
        } else {
          scrollTop =
            window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
          scrollHeight = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
          );
          clientHeight = window.innerHeight;
        }

        const scrollable = scrollHeight - clientHeight;
        if (scrollable <= 0) return;
        const rawPct = Math.min(100, Math.round((scrollTop / scrollable) * 100));
        updateQuartile(rawPct);
      };

      document.addEventListener('scroll', onScroll, { capture: true, passive: true });
      cleanupScroll = () => document.removeEventListener('scroll', onScroll, { capture: true });
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        activeTimeMs.current += Date.now() - lastVisibleAt.current;
      } else {
        lastVisibleAt.current = Date.now();
      }
    };

    const getActiveSeconds = (): number => {
      const pendingMs =
        document.visibilityState === 'hidden' ? 0 : Date.now() - lastVisibleAt.current;
      return Math.round((activeTimeMs.current + pendingMs) / 1000);
    };

    const firePageClosed = (viaSyncPostMessage = false) => {
      if (hasFired.current) return;
      hasFired.current = true;

      const closedPayload = {
        ...basePayload,
        session_duration_seconds: getActiveSeconds(),
        max_scroll_percentage: maxScrollQuartile.current,
      };

      if (viaSyncPostMessage) {
        // pagehide cancels async XHR; postMessage is synchronous and survives tab close.
        // EventReceiver expects { message: { action: 'blipEvent:<action>', content } }.
        let parentOrigin = '*';
        try {
          const origin = new URL(document.referrer).origin;
          if (origin !== 'null') parentOrigin = origin;
        } catch { /* keep '*' */ }
        window.parent.postMessage(
          {
            message: {
              action: 'blipEvent:segment',
              content: {
                method: 'createTrack',
                parameters: {
                  trackEvent: closedEvent,
                  payload: closedPayload,
                },
              },
            },
          },
          parentOrigin,
        );
      } else {
        createTrack(tenant, closedEvent, closedPayload);
      }
    };

    const onUnload = () => firePageClosed(true);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onUnload);

    return () => {
      cleanupIO?.();
      cleanupScroll?.();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onUnload);
      firePageClosed(false);
    };
  }, []);
};
